/*
 * JaJa Method — interactive "Where We've Worked" globe (reusable section engine)
 * -----------------------------------------------------------------------------
 * Loaded once via <script type="module"> (ES modules dedupe by URL, so multiple
 * globe sections on one page share this single file). It:
 *   1. lazy-loads d3 + topojson-client from CDN once (shared across instances),
 *   2. initialises every `.jaja-globe-section` on the page,
 *   3. re-initialises sections added live in the Shopify theme editor.
 *
 * Each section renders its own config as JSON in `.jaja-globe-config`, and all
 * DOM queries are scoped to the section root, so any number of globes can live
 * on the same page without colliding.
 *
 * Renderer: real-world geography via d3-geo + the public topojson/world-atlas
 * dataset, drawn as an orthographic ("rotating globe") SVG projection. Pins use
 * the exact same projection() call as the coastlines, so they stay aligned.
 */
(function () {
  "use strict";

  var D3_URL = "https://cdnjs.cloudflare.com/ajax/libs/d3/7.9.0/d3.min.js";
  var TOPOJSON_URL = "https://cdn.jsdelivr.net/npm/topojson-client@3/dist/topojson-client.min.js";
  var WORLD_ATLAS_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/land-110m.json";
  var COUNTRY_ATLAS_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

  // Default locations — used when a section defines no location blocks.
  var DEFAULT_LOCATIONS = [
    { name: "Taipei, Taiwan", lat: 25.0330, lon: 121.5654 },
    { name: "Australia", lat: -33.8688, lon: 151.2093 },
    { name: "Netherlands", lat: 52.3676, lon: 4.9041 },
    { name: "Spain", lat: 40.4168, lon: -3.7038 },
    { name: "Switzerland", lat: 47.3769, lon: 8.5417 },
    { name: "Portugal", lat: 38.7223, lon: -9.1393 },
    { name: "Romania", lat: 44.4268, lon: 26.1025 },
    { name: "France", lat: 48.8566, lon: 2.3522 },
    { name: "Italy", lat: 41.9028, lon: 12.4964 },
    { name: "Croatia", lat: 45.8150, lon: 15.9819 },
    { name: "Canada", lat: 43.6532, lon: -79.3832 },
    { name: "California – Rancho Santa Fe", lat: 33.0192, lon: -117.2028 },
    { name: "California – Newport", lat: 33.6189, lon: -117.9298 },
    { name: "Florida", lat: 28.5383, lon: -81.3792 },
    { name: "New York", lat: 40.7128, lon: -74.0060 },
    { name: "New Jersey", lat: 40.0583, lon: -74.4057 },
    { name: "Idaho", lat: 44.0682, lon: -114.7420 },
    { name: "Puerto Rico", lat: 18.2208, lon: -66.5901 },
    { name: "Texas", lat: 31.9686, lon: -99.9018 },
    { name: "Iowa", lat: 41.8780, lon: -93.0977 },
    { name: "Chicago", lat: 41.8781, lon: -87.6298 },
    { name: "Colorado", lat: 39.5501, lon: -105.7821 },
    { name: "North Carolina", lat: 35.7596, lon: -79.0193 },
    { name: "Arkansas", lat: 34.9697, lon: -92.3731 },
    { name: "Massachusetts – Boston", lat: 42.3601, lon: -71.0589 },
    { name: "Georgia – Atlanta", lat: 33.7490, lon: -84.3880 },
    { name: "Washington – Seattle", lat: 47.6062, lon: -122.3321 },
    { name: "Michigan", lat: 44.3148, lon: -85.6024 },
    { name: "Wyoming", lat: 43.0759, lon: -107.2903 },
    { name: "Louisiana", lat: 30.9843, lon: -91.9623 }
  ];

  // ISO 3166-1 numeric id -> common country name (for the country labels).
  var COUNTRY_NAMES = {"004":"Afghanistan","008":"Albania","010":"Antarctica","012":"Algeria","016":"American Samoa","020":"Andorra","024":"Angola","028":"Antigua and Barbuda","031":"Azerbaijan","032":"Argentina","036":"Australia","040":"Austria","044":"Bahamas","048":"Bahrain","050":"Bangladesh","051":"Armenia","052":"Barbados","056":"Belgium","060":"Bermuda","064":"Bhutan","068":"Bolivia","070":"Bosnia and Herzegovina","072":"Botswana","074":"Bouvet Island","076":"Brazil","084":"Belize","086":"British Indian Ocean Territory","090":"Solomon Islands","092":"Virgin Islands (British)","096":"Brunei","100":"Bulgaria","104":"Myanmar","108":"Burundi","112":"Belarus","116":"Cambodia","120":"Cameroon","124":"Canada","132":"Cabo Verde","136":"Cayman Islands","140":"Central African Republic","144":"Sri Lanka","148":"Chad","152":"Chile","156":"China","158":"Taiwan","162":"Christmas Island","166":"Cocos (Keeling) Islands","170":"Colombia","174":"Comoros","175":"Mayotte","178":"Congo","180":"DR Congo","184":"Cook Islands","188":"Costa Rica","191":"Croatia","192":"Cuba","196":"Cyprus","203":"Czechia","204":"Benin","208":"Denmark","212":"Dominica","214":"Dominican Republic","218":"Ecuador","222":"El Salvador","226":"Equatorial Guinea","231":"Ethiopia","232":"Eritrea","233":"Estonia","234":"Faroe Islands","238":"Falkland Islands","239":"South Georgia and the South Sandwich Islands","242":"Fiji","246":"Finland","248":"Åland Islands","250":"France","254":"French Guiana","258":"French Polynesia","260":"French Southern Territories","262":"Djibouti","266":"Gabon","268":"Georgia","270":"Gambia","275":"Palestine","276":"Germany","288":"Ghana","292":"Gibraltar","296":"Kiribati","300":"Greece","304":"Greenland","308":"Grenada","312":"Guadeloupe","316":"Guam","320":"Guatemala","324":"Guinea","328":"Guyana","332":"Haiti","334":"Heard Island and McDonald Islands","336":"Holy See","340":"Honduras","344":"Hong Kong","348":"Hungary","352":"Iceland","356":"India","360":"Indonesia","364":"Iran","368":"Iraq","372":"Ireland","376":"Israel","380":"Italy","384":"Ivory Coast","388":"Jamaica","392":"Japan","398":"Kazakhstan","400":"Jordan","404":"Kenya","408":"North Korea","410":"South Korea","414":"Kuwait","417":"Kyrgyzstan","418":"Laos","422":"Lebanon","426":"Lesotho","428":"Latvia","430":"Liberia","434":"Libya","438":"Liechtenstein","440":"Lithuania","442":"Luxembourg","446":"Macao","450":"Madagascar","454":"Malawi","458":"Malaysia","462":"Maldives","466":"Mali","470":"Malta","474":"Martinique","478":"Mauritania","480":"Mauritius","484":"Mexico","492":"Monaco","496":"Mongolia","498":"Moldova","499":"Montenegro","500":"Montserrat","504":"Morocco","508":"Mozambique","512":"Oman","516":"Namibia","520":"Nauru","524":"Nepal","528":"Netherlands","531":"Curaçao","533":"Aruba","534":"Sint Maarten (Dutch part)","535":"Bonaire, Sint Eustatius and Saba","540":"New Caledonia","548":"Vanuatu","554":"New Zealand","558":"Nicaragua","562":"Niger","566":"Nigeria","570":"Niue","574":"Norfolk Island","578":"Norway","580":"Northern Mariana Islands","581":"United States Minor Outlying Islands","583":"Micronesia","584":"Marshall Islands","585":"Palau","586":"Pakistan","591":"Panama","598":"Papua New Guinea","600":"Paraguay","604":"Peru","608":"Philippines","612":"Pitcairn","616":"Poland","620":"Portugal","624":"Guinea-Bissau","626":"Timor-Leste","630":"Puerto Rico","634":"Qatar","638":"Réunion","642":"Romania","643":"Russia","646":"Rwanda","652":"Saint Barthélemy","654":"Saint Helena, Ascension and Tristan da Cunha","659":"Saint Kitts and Nevis","660":"Anguilla","662":"Saint Lucia","663":"Saint Martin (French part)","666":"Saint Pierre and Miquelon","670":"Saint Vincent and the Grenadines","674":"San Marino","678":"São Tomé and Príncipe","682":"Saudi Arabia","686":"Senegal","688":"Serbia","690":"Seychelles","694":"Sierra Leone","702":"Singapore","703":"Slovakia","704":"Vietnam","705":"Slovenia","706":"Somalia","710":"South Africa","716":"Zimbabwe","724":"Spain","728":"South Sudan","729":"Sudan","732":"Western Sahara","740":"Suriname","744":"Svalbard and Jan Mayen","748":"Eswatini","752":"Sweden","756":"Switzerland","760":"Syria","762":"Tajikistan","764":"Thailand","768":"Togo","772":"Tokelau","776":"Tonga","780":"Trinidad and Tobago","784":"United Arab Emirates","788":"Tunisia","792":"Turkey","795":"Turkmenistan","796":"Turks and Caicos Islands","798":"Tuvalu","800":"Uganda","804":"Ukraine","807":"North Macedonia","818":"Egypt","826":"United Kingdom","831":"Guernsey","832":"Jersey","833":"Isle of Man","834":"Tanzania","840":"United States","850":"Virgin Islands (U.S.)","854":"Burkina Faso","858":"Uruguay","860":"Uzbekistan","862":"Venezuela","876":"Wallis and Futuna","882":"Samoa","887":"Yemen","894":"Zambia"};

  // -------------------------------------------------------------------------
  // Shared CDN dependency loader (d3 + topojson), resolved once for the page.
  // -------------------------------------------------------------------------
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-jaja-dep="' + src + '"]');
      if (existing) {
        if (existing.getAttribute("data-loaded")) { resolve(); }
        else {
          existing.addEventListener("load", function () { resolve(); });
          existing.addEventListener("error", reject);
        }
        return;
      }
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.setAttribute("data-jaja-dep", src);
      s.addEventListener("load", function () { s.setAttribute("data-loaded", "1"); resolve(); });
      s.addEventListener("error", reject);
      document.head.appendChild(s);
    });
  }

  var depsPromise = null;
  function loadDeps() {
    if (!depsPromise) {
      depsPromise = loadScript(D3_URL).then(function () { return loadScript(TOPOJSON_URL); });
    }
    return depsPromise;
  }

  // -------------------------------------------------------------------------
  // Init: one section root -> one globe.
  // -------------------------------------------------------------------------
  function initGlobe(root) {
    if (!root || root.__jajaGlobeInited) return;
    root.__jajaGlobeInited = true;

    var config = {};
    var cfgEl = root.querySelector(".jaja-globe-config");
    if (cfgEl) {
      try { config = JSON.parse(cfgEl.textContent); } catch (e) { config = {}; }
    }

    loadDeps()
      .then(function () { runGlobe(root, config); })
      .catch(function (err) {
        var l = root.querySelector(".jaja-globe-loading");
        if (l) l.textContent = "Could not load map data (check internet connection).";
        console.error("JaJa globe: dependency load failed", err);
      });
  }

  // -------------------------------------------------------------------------
  // The globe itself — all state closed over per instance, all DOM scoped to
  // `root`, so multiple instances never interfere.
  // -------------------------------------------------------------------------
  function runGlobe(root, config) {
    var d3 = window.d3;
    var topojson = window.topojson;
    if (!d3 || !topojson) { console.error("JaJa globe: d3/topojson unavailable"); return; }

    var wrap = root;
    var svgEl = root.querySelector(".jaja-globe-svg");
    var tooltip = root.querySelector(".jaja-globe-tooltip");
    var loadingEl = root.querySelector(".jaja-globe-loading");
    var svg = d3.select(svgEl);

    var LOCATIONS = (config.locations && config.locations.length) ? config.locations : DEFAULT_LOCATIONS;
    var orange = config.colorOrange || "#ff6d00";
    var pink = config.colorPink || "#c10ac6";
    var red = config.colorRed || "#e84849";

    // Defs: gradients + glow filters (scoped ids so instances don't clash)
    var uid = "jg-" + (config.uid || Math.random().toString(36).slice(2, 9));
    var defs = svg.append("defs");

    var oceanGrad = defs.append("radialGradient").attr("id", uid + "-ocean");
    oceanGrad.append("stop").attr("offset", "0%").attr("stop-color", "#eef2fd");
    oceanGrad.append("stop").attr("offset", "70%").attr("stop-color", "#dbe4fa");
    oceanGrad.append("stop").attr("offset", "100%").attr("stop-color", "#c3d2f5");

    var rimGrad = defs.append("linearGradient").attr("id", uid + "-rim")
      .attr("x1", "0%").attr("y1", "0%").attr("x2", "100%").attr("y2", "100%");
    rimGrad.append("stop").attr("offset", "0%").attr("stop-color", pink);
    rimGrad.append("stop").attr("offset", "50%").attr("stop-color", red);
    rimGrad.append("stop").attr("offset", "100%").attr("stop-color", orange);

    var glowFilter = defs.append("filter").attr("id", uid + "-pinglow")
      .attr("x", "-200%").attr("y", "-200%").attr("width", "500%").attr("height", "500%");
    glowFilter.append("feGaussianBlur").attr("stdDeviation", 2.4).attr("result", "blur");

    var atmosphereFilter = defs.append("filter").attr("id", uid + "-atmo")
      .attr("x", "-150%").attr("y", "-150%").attr("width", "400%").attr("height", "400%");
    atmosphereFilter.append("feGaussianBlur").attr("stdDeviation", 9);

    var coastGlowFilter = defs.append("filter").attr("id", uid + "-coast")
      .attr("x", "-80%").attr("y", "-80%").attr("width", "260%").attr("height", "260%");
    coastGlowFilter.append("feGaussianBlur").attr("stdDeviation", 1.6);

    var sphereG = svg.append("g");
    var graticuleG = svg.append("g");
    var landGlowG = svg.append("g");
    var landG = svg.append("g");
    var countryBorderG = svg.append("g");
    var countryLabelsG = svg.append("g");
    var rimG = svg.append("g");
    var pinsG = svg.append("g");

    var spherePath = sphereG.append("path").attr("fill", "url(#" + uid + "-ocean)");

    rimG.style("mix-blend-mode", "multiply");
    var rimGlowOuter = rimG.append("path").attr("fill", "none").attr("stroke", "url(#" + uid + "-rim)")
      .attr("stroke-width", 26).attr("opacity", 0.55).attr("filter", "url(#" + uid + "-atmo)");
    var rimGlowInner = rimG.append("path").attr("fill", "none").attr("stroke", "url(#" + uid + "-rim)")
      .attr("stroke-width", 10).attr("opacity", 0.68).attr("filter", "url(#" + uid + "-atmo)");
    var rimPath = rimG.append("path").attr("fill", "none").attr("stroke", "url(#" + uid + "-rim)")
      .attr("stroke-width", 1.4).attr("opacity", 0.75);

    var graticulePath = graticuleG.append("path").attr("fill", "none")
      .attr("stroke", "#ff8a3d").attr("stroke-width", 0.6).attr("opacity", 0.3);

    landGlowG.style("mix-blend-mode", "multiply");
    var landGlowPath = landGlowG.append("path").attr("fill", "none").attr("stroke", "url(#" + uid + "-rim)")
      .attr("stroke-width", 2.6).attr("opacity", 0.68).attr("filter", "url(#" + uid + "-coast)");

    var landPath = landG.append("path")
      .attr("fill", "rgba(255,228,190,0.97)")
      .attr("stroke", "rgba(255,109,0,0.65)").attr("stroke-width", 0.8);

    var countryBorderPath = countryBorderG.append("path").attr("fill", "none")
      .attr("stroke", "rgba(120,60,20,0.45)").attr("stroke-width", 0.6).attr("opacity", 0.75);

    // Projection + geo path generator
    var projection = d3.geoOrthographic().clipAngle(90).precision(0.3);
    var geoPath = d3.geoPath(projection);
    var graticule = d3.geoGraticule().step([20, 20])();

    var width = 0, height = 0;
    var baseScale = 1;
    var MIN_ZOOM_MULT = 1, MAX_ZOOM_MULT = config.maxZoom || 3.1;
    var currentZoomMult = 1;

    function sizeProjection() {
      width = wrap.clientWidth;
      height = wrap.clientHeight;
      if (!width || !height) return;
      svg.attr("viewBox", "0 0 " + width + " " + height);
      baseScale = Math.min(width, height) * 0.235;
      projection.translate([width / 2, height / 2]).scale(baseScale * currentZoomMult);
      redrawSphere();
    }

    // Pins
    var COLOR_ORANGE = d3.rgb(orange);
    var COLOR_PINK = d3.rgb(pink);
    var colorScale = d3.interpolateRgb(COLOR_ORANGE, COLOR_PINK);

    var hoveredPin = null;
    var lockedPin = null;

    var pins = LOCATIONS.map(function (loc, i) {
      var t = LOCATIONS.length > 1 ? i / (LOCATIONS.length - 1) : 0;
      var color = colorScale(t);

      var g = pinsG.append("g").attr("class", "jaja-pin");

      var glow = g.append("circle").attr("r", 9).attr("fill", color)
        .attr("opacity", 0.72).attr("filter", "url(#" + uid + "-pinglow)");
      var ring = g.append("circle").attr("r", 4).attr("fill", "none")
        .attr("stroke", color).attr("stroke-width", 1.4).attr("opacity", 0);
      var dot = g.append("circle").attr("r", 3.2).attr("fill", color)
        .attr("stroke", "#fff8ec").attr("stroke-width", 0.8);

      var label = pinsG.append("text").attr("font-size", 10.5).attr("font-weight", 600)
        .attr("fill", "rgba(58,18,64,0.9)").attr("text-anchor", "middle").attr("opacity", 0)
        .style("paint-order", "stroke").style("stroke", "rgba(255,251,240,0.9)")
        .style("stroke-width", "3px").text(loc.name);

      var entry = { loc: loc, g: g, glow: glow, ring: ring, dot: dot, label: label, pulsePhase: Math.random() * Math.PI * 2 };

      g.on("pointerenter", function (event) {
        if (lockedPin) return;
        hoveredPin = entry;
        dot.attr("r", 5);
        svgEl.style.cursor = "pointer";
        showTooltip(loc.name, event.clientX, event.clientY);
      }).on("pointerleave", function () {
        if (lockedPin) return;
        hoveredPin = null;
        dot.attr("r", 3.2);
        svgEl.style.cursor = "grab";
        hideTooltip();
      }).on("pointermove", function (event) {
        if (lockedPin) return;
        showTooltip(loc.name, event.clientX, event.clientY);
      }).on("click", function (event) {
        event.stopPropagation();
        if (lockedPin === entry) {
          lockedPin = null; dot.attr("r", 3.2); hideTooltip();
        } else {
          if (lockedPin) lockedPin.dot.attr("r", 3.2);
          lockedPin = entry; dot.attr("r", 5.5);
          showTooltip(loc.name, event.clientX, event.clientY);
        }
      });

      return entry;
    });

    function showTooltip(name, clientX, clientY) {
      var rect = wrap.getBoundingClientRect();
      tooltip.textContent = name;
      tooltip.style.left = (clientX - rect.left) + "px";
      tooltip.style.top = (clientY - rect.top) + "px";
      tooltip.classList.add("visible");
    }
    function hideTooltip() { tooltip.classList.remove("visible"); }

    // Country name labels
    var countryLabels = [];

    function largestPolygonCoords(geometry) {
      if (!geometry) return null;
      if (geometry.type === "Polygon") return geometry.coordinates;
      if (geometry.type === "MultiPolygon") {
        var best = null, bestArea = -Infinity;
        geometry.coordinates.forEach(function (coords) {
          var a = d3.geoArea({ type: "Polygon", coordinates: coords });
          if (a > bestArea) { bestArea = a; best = coords; }
        });
        return best;
      }
      return null;
    }

    function findInteriorLabelPoint(coords) {
      var poly = { type: "Polygon", coordinates: coords };
      var bounds;
      try { bounds = d3.geoBounds(poly); } catch (e) { return null; }
      if (!bounds) return null;
      var lon0 = bounds[0][0], lat0 = bounds[0][1];
      var lon1 = bounds[1][0], lat1 = bounds[1][1];
      if (lon1 < lon0) lon1 += 360;

      var ring = coords[0] || [];
      var step = Math.max(1, Math.floor(ring.length / 60));
      var boundaryPts = [];
      for (var i = 0; i < ring.length; i += step) boundaryPts.push(ring[i]);
      if (!boundaryPts.length) return null;

      function minDistToBoundary(lon, lat) {
        var min = Infinity;
        for (var j = 0; j < boundaryPts.length; j++) {
          var d = d3.geoDistance([lon, lat], boundaryPts[j]);
          if (d < min) min = d;
        }
        return min;
      }
      function isInside(lon, lat) {
        var testLon = ((lon + 540) % 360) - 180;
        return d3.geoContains(poly, [testLon, lat]);
      }
      function gridSearch(minLon, maxLon, minLat, maxLat, divisions) {
        var bestPt = null, bestScore = -Infinity;
        for (var gx = 0; gx <= divisions; gx++) {
          for (var gy = 0; gy <= divisions; gy++) {
            var lon = minLon + (maxLon - minLon) * (gx / divisions);
            var lat = minLat + (maxLat - minLat) * (gy / divisions);
            var normLon = ((lon + 540) % 360) - 180;
            if (!isInside(lon, lat)) continue;
            var score = minDistToBoundary(normLon, lat);
            if (score > bestScore) { bestScore = score; bestPt = [normLon, lat]; }
          }
        }
        return bestPt;
      }

      var coarse = gridSearch(lon0, lon1, lat0, lat1, 8);
      if (!coarse) return null;
      var span = Math.max((lon1 - lon0), (lat1 - lat0)) / 8;
      var refined = gridSearch(coarse[0] - span, coarse[0] + span, coarse[1] - span, coarse[1] + span, 6);
      return refined || coarse;
    }

    function buildCountryLabels(countryFeatureCollection) {
      var features = countryFeatureCollection.features;
      var areas = features.map(function (f) { return d3.geoArea(f); });
      var minLog = Infinity, maxLog = -Infinity;
      areas.forEach(function (a) {
        if (a <= 0) return;
        var l = Math.log(a);
        if (l < minLog) minLog = l;
        if (l > maxLog) maxLog = l;
      });

      features.forEach(function (f, i) {
        var name = COUNTRY_NAMES[f.id];
        if (!name) return;
        var area = areas[i];
        if (area <= 0) return;

        var coords = largestPolygonCoords(f.geometry);
        var point = coords ? findInteriorLabelPoint(coords) : null;
        if (!point) point = d3.geoCentroid(f);
        if (!point || isNaN(point[0]) || isNaN(point[1])) return;

        var l = Math.log(area);
        var sizeFactor = (maxLog > minLog) ? (l - minLog) / (maxLog - minLog) : 0.5;

        var label = countryLabelsG.append("text").attr("font-size", 8).attr("font-weight", 400)
          .attr("fill", "rgba(74,42,20,0.85)").attr("text-anchor", "middle").attr("opacity", 0)
          .style("pointer-events", "none").text(name);

        countryLabels.push({ lon: point[0], lat: point[1], sizeFactor: sizeFactor, label: label });
      });
    }

    // Redraw
    var landFeature = null;
    var countryMesh = null;

    // Sphere + rim glow are rotation-invariant (they only change on zoom/resize),
    // so they're redrawn on demand instead of every frame — this stops the costly
    // atmosphere blur filter from re-running on every rotation tick.
    function redrawSphere() {
      var sphereD = geoPath({ type: "Sphere" });
      spherePath.attr("d", sphereD);
      rimGlowOuter.attr("d", sphereD);
      rimGlowInner.attr("d", sphereD);
      rimPath.attr("d", sphereD);
    }

    function redraw(t) {
      graticulePath.attr("d", geoPath(graticule));
      if (landFeature) {
        var landD = geoPath(landFeature);
        landGlowPath.attr("d", landD);
        landPath.attr("d", landD);
      }
      if (countryMesh) countryBorderPath.attr("d", geoPath(countryMesh));

      var center = [-projection.rotate()[0], -projection.rotate()[1]];
      var zoomT = (currentZoomMult - MIN_ZOOM_MULT) / (MAX_ZOOM_MULT - MIN_ZOOM_MULT);
      zoomT = Math.max(0, Math.min(1, zoomT));
      var zoomOpacity = Math.max(0, Math.min(1, (zoomT - 0.4) / 0.35));

      pins.forEach(function (p) {
        var dist = d3.geoDistance([p.loc.lon, p.loc.lat], center);
        var frontFactor = Math.max(0, Math.min(1, (Math.PI / 2 - dist) / (Math.PI / 2) * 3));
        if (frontFactor <= 0.01) {
          if (!p.hidden) { p.g.attr("opacity", 0); p.label.attr("opacity", 0); p.hidden = true; }
          return;
        }
        p.hidden = false;

        var xy = projection([p.loc.lon, p.loc.lat]);
        p.g.attr("opacity", frontFactor).attr("transform", "translate(" + xy[0] + "," + xy[1] + ")");

        var phase = ((t || 0) * 0.0008 + p.pulsePhase) % (Math.PI * 2);
        var s = 4 + (phase / (Math.PI * 2)) * 16;
        var op = Math.max(0, 0.5 * (1 - phase / (Math.PI * 2))) * frontFactor;
        p.ring.attr("r", s).attr("opacity", op);

        var labelOpacity = zoomOpacity * frontFactor;
        p.label.attr("x", xy[0]).attr("y", xy[1] - 12).attr("opacity", labelOpacity);
      });

      var countryThreshold = 0.62 - zoomT * 0.5;
      countryLabels.forEach(function (c) {
        // Cheap zoom gate first: at world view most countries are below the size
        // threshold, so we skip the geoDistance/projection work for them entirely.
        var sizeOpacity = Math.max(0, Math.min(1, (c.sizeFactor - countryThreshold) / 0.12));
        if (sizeOpacity <= 0.01) { if (!c.hidden) { c.label.attr("opacity", 0); c.hidden = true; } return; }
        var dist = d3.geoDistance([c.lon, c.lat], center);
        var frontFactor = Math.max(0, Math.min(1, (Math.PI / 2 - dist) / (Math.PI / 2) * 3));
        var opacity = sizeOpacity * frontFactor;
        if (opacity <= 0.01) { if (!c.hidden) { c.label.attr("opacity", 0); c.hidden = true; } return; }
        var xy = projection([c.lon, c.lat]);
        c.label.attr("x", xy[0]).attr("y", xy[1]).attr("opacity", opacity);
        c.hidden = false;
      });
    }

    // Interaction
    var isDragging = false;
    var lastX = 0, lastY = 0;
    var autoRotate = config.autoRotate !== false;
    var idleTimer = null;

    function setIdleResume() {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { autoRotate = true; }, 2200);
    }

    svgEl.addEventListener("pointerdown", function (e) {
      isDragging = true; autoRotate = false;
      lastX = e.clientX; lastY = e.clientY;
      try { svgEl.setPointerCapture(e.pointerId); } catch (err) {}
    });
    svgEl.addEventListener("pointermove", function (e) {
      if (!isDragging) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      var r = projection.rotate();
      var sensitivity = 60 / (baseScale * currentZoomMult / 100 + 40);
      var lambda = r[0] + dx * 0.28 * sensitivity;
      var phi = Math.max(-90, Math.min(90, r[1] - dy * 0.28 * sensitivity));
      projection.rotate([lambda, phi, r[2]]);
    });
    window.addEventListener("pointerup", function () {
      if (isDragging) { isDragging = false; setIdleResume(); }
    });

    svgEl.addEventListener("wheel", function (e) {
      e.preventDefault();
      autoRotate = false;
      currentZoomMult += e.deltaY * -0.0016;
      currentZoomMult = Math.max(MIN_ZOOM_MULT, Math.min(MAX_ZOOM_MULT, currentZoomMult));
      projection.scale(baseScale * currentZoomMult);
      redrawSphere();
      setIdleResume();
    }, { passive: false });

    var lastPinchDist = null;
    svgEl.addEventListener("touchmove", function (e) {
      if (e.touches.length === 2) {
        var dx = e.touches[0].clientX - e.touches[1].clientX;
        var dy = e.touches[0].clientY - e.touches[1].clientY;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (lastPinchDist !== null) {
          var delta = dist - lastPinchDist;
          currentZoomMult += delta * 0.004;
          currentZoomMult = Math.max(MIN_ZOOM_MULT, Math.min(MAX_ZOOM_MULT, currentZoomMult));
          projection.scale(baseScale * currentZoomMult);
          redrawSphere();
        }
        lastPinchDist = dist; autoRotate = false;
      }
    }, { passive: true });
    svgEl.addEventListener("touchend", function () { lastPinchDist = null; setIdleResume(); });

    svgEl.addEventListener("click", function () {
      if (lockedPin) { lockedPin.dot.attr("r", 3.2); lockedPin = null; hideTooltip(); }
    });

    function onResize() { sizeProjection(); redraw(); }
    window.addEventListener("resize", onResize);
    if (window.ResizeObserver) { new ResizeObserver(onResize).observe(wrap); }

    // Pause all rendering while the globe is scrolled out of view.
    var isVisible = true;
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        isVisible = entries[entries.length - 1].isIntersecting;
      }, { threshold: 0 }).observe(wrap);
    }

    function animate(t) {
      requestAnimationFrame(animate);
      if (!isVisible) return;
      if (autoRotate && !isDragging) {
        var r = projection.rotate();
        projection.rotate([r[0] + 0.09, r[1], r[2]]);
      }
      redraw(t);
    }

    // Boot
    sizeProjection();
    projection.rotate([60, -25, 0]);
    redraw(0);
    animate(0);

    fetch(WORLD_ATLAS_URL)
      .then(function (res) { if (!res.ok) throw new Error("HTTP " + res.status); return res.json(); })
      .then(function (world) {
        landFeature = topojson.feature(world, world.objects.land);
        if (loadingEl) loadingEl.style.display = "none";
        redraw(0);
      })
      .catch(function (err) {
        if (loadingEl) loadingEl.textContent = "Could not load map data (check internet connection).";
        console.error("JaJa globe: failed to load world-atlas data", err);
      });

    fetch(COUNTRY_ATLAS_URL)
      .then(function (res) { if (!res.ok) throw new Error("HTTP " + res.status); return res.json(); })
      .then(function (world) {
        var countries = topojson.feature(world, world.objects.countries);
        buildCountryLabels(countries);
        countryMesh = topojson.mesh(world, world.objects.countries, function (a, b) { return a !== b; });
        redraw(0);
      })
      .catch(function (err) { console.error("JaJa globe: failed to load country label data", err); });
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll(".jaja-globe-section");
    Array.prototype.forEach.call(nodes, initGlobe);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(); });
  } else {
    initAll();
  }
  // Shopify theme editor: (re)initialise sections added/updated live.
  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });
})();
